/**
 * Barème des sanctions du Code de la route — usage pédagogique.
 *
 * Source : Code de la route en vigueur (Légifrance), Code des assurances,
 * Code pénal. Données vérifiées au 1er janvier 2026.
 *
 * Important : ce barème est PÉDAGOGIQUE. La sanction réelle dépend des
 * circonstances, du juge ou de l'OPJ et peut varier (récidive, cumul,
 * majoration, contestation). Les amendes forfaitaires sont les montants
 * de référence (minorée → forfaitaire → majorée).
 */

export const SANCTION_CATEGORIES = [
  { id: 'all', label: 'Toutes', color: '#0f1116' },
  { id: 'vitesse', label: 'Vitesse', color: '#b91c1c' },
  { id: 'alcool', label: 'Alcool & stupéfiants', color: '#7c3aed' },
  { id: 'priorite', label: 'Priorités & signalisation', color: '#1d4ed8' },
  { id: 'equipement', label: 'Équipement', color: '#16a34a' },
  { id: 'telephone', label: 'Téléphone & distraction', color: '#f59e0b' },
  { id: 'documents', label: 'Documents & administratif', color: '#0891b2' },
  { id: 'vehicule', label: 'Véhicule & technique', color: '#db2777' },
  { id: 'delits', label: 'Délits routiers', color: '#dc2626' },
];

/**
 * Chaque entrée :
 *  - id           : identifiant stable (slug)
 *  - cat          : catégorie (cf. SANCTION_CATEGORIES.id)
 *  - infraction   : libellé court
 *  - article      : référence légale (Code de la route sauf mention)
 *  - amende       : montant ou plage
 *  - classe       : classe contraventionnelle (1 à 5) ou 'Délit'
 *  - points       : nombre de points retirés (négatif) ou null
 *  - complement   : sanctions complémentaires (suspension, immobilisation, prison…)
 *  - note         : commentaire pédagogique (optionnel)
 */
export const SANCTIONS = [
  // === VITESSE ===
  {
    id: 'vitesse-moins-20-hors',
    cat: 'vitesse',
    infraction: 'Excès de vitesse < 20 km/h (hors agglomération)',
    article: 'R413-14',
    amende: '68 € (minoré 45 € · majoré 180 €)',
    classe: '3',
    points: -1,
    complement: '—',
  },
  {
    id: 'vitesse-moins-20-agglo',
    cat: 'vitesse',
    infraction: 'Excès de vitesse < 20 km/h (en agglomération)',
    article: 'R413-14-1',
    amende: '135 € (minoré 90 € · majoré 375 €)',
    classe: '4',
    points: -1,
    complement: '—',
  },
  {
    id: 'vitesse-20-30',
    cat: 'vitesse',
    infraction: 'Excès de vitesse 20 à 29 km/h',
    article: 'R413-14',
    amende: '135 € (minoré 90 € · majoré 375 €)',
    classe: '4',
    points: -2,
    complement: '—',
  },
  {
    id: 'vitesse-30-40',
    cat: 'vitesse',
    infraction: 'Excès de vitesse 30 à 39 km/h',
    article: 'R413-14',
    amende: '135 € (minoré 90 € · majoré 375 €)',
    classe: '4',
    points: -3,
    complement: 'Suspension du permis jusqu\'à 3 ans',
  },
  {
    id: 'vitesse-40-50',
    cat: 'vitesse',
    infraction: 'Excès de vitesse 40 à 49 km/h',
    article: 'R413-14',
    amende: '135 € (minoré 90 € · majoré 375 €)',
    classe: '4',
    points: -4,
    complement: 'Suspension jusqu\'à 3 ans, stage obligatoire',
  },
  {
    id: 'vitesse-50-plus',
    cat: 'vitesse',
    infraction: 'Excès de vitesse ≥ 50 km/h',
    article: 'R413-14-1',
    amende: '1 500 € (3 000 € en récidive)',
    classe: '5',
    points: -6,
    complement: 'Suspension jusqu\'à 3 ans, immobilisation, confiscation possible en récidive',
    note: 'Délit en cas de récidive dans les 3 ans.',
  },

  // === ALCOOL & STUPÉFIANTS ===
  {
    id: 'alcool-probatoire',
    cat: 'alcool',
    infraction: 'Conduite avec ≥ 0,2 g/L sang (permis probatoire ou apprenti)',
    article: 'R234-1',
    amende: '135 € forfaitaire',
    classe: '4',
    points: -6,
    complement: 'Suspension du permis, immobilisation',
    note: 'Tolérance zéro pour les jeunes conducteurs et permis probatoire.',
  },
  {
    id: 'alcool-contraventionnel',
    cat: 'alcool',
    infraction: 'Alcoolémie 0,5 à 0,8 g/L sang (0,25 à 0,4 mg/L air expiré)',
    article: 'R234-1',
    amende: '135 € forfaitaire',
    classe: '4',
    points: -6,
    complement: 'Suspension jusqu\'à 3 ans, stage obligatoire',
  },
  {
    id: 'alcool-delit',
    cat: 'alcool',
    infraction: 'Alcoolémie ≥ 0,8 g/L sang (≥ 0,4 mg/L air expiré) ou état d\'ivresse manifeste',
    article: 'L234-1',
    amende: '4 500 € + 2 ans de prison',
    classe: 'Délit',
    points: -6,
    complement: 'Suspension jusqu\'à 3 ans, annulation possible, confiscation, EAD (éthylotest anti-démarrage)',
  },
  {
    id: 'alcool-refus',
    cat: 'alcool',
    infraction: 'Refus de se soumettre au dépistage d\'alcoolémie',
    article: 'L234-8',
    amende: '4 500 € + 2 ans de prison',
    classe: 'Délit',
    points: -6,
    complement: 'Suspension, annulation possible',
  },
  {
    id: 'stups',
    cat: 'alcool',
    infraction: 'Conduite après usage de stupéfiants',
    article: 'L235-1',
    amende: '4 500 € + 2 ans de prison',
    classe: 'Délit',
    points: -6,
    complement: 'Suspension jusqu\'à 3 ans, annulation possible, confiscation',
  },

  // === PRIORITÉS & SIGNALISATION ===
  {
    id: 'feu-rouge',
    cat: 'priorite',
    infraction: 'Non-respect d\'un feu rouge',
    article: 'R412-30',
    amende: '135 € (minoré 90 € · majoré 375 €)',
    classe: '4',
    points: -4,
    complement: 'Suspension jusqu\'à 3 ans',
  },
  {
    id: 'stop',
    cat: 'priorite',
    infraction: 'Non-respect d\'un panneau STOP',
    article: 'R415-6',
    amende: '135 €',
    classe: '4',
    points: -4,
    complement: 'Suspension jusqu\'à 3 ans',
  },
  {
    id: 'cedez-passage',
    cat: 'priorite',
    infraction: 'Non-respect d\'un « Cédez le passage »',
    article: 'R415-7',
    amende: '135 €',
    classe: '4',
    points: -4,
    complement: 'Suspension jusqu\'à 3 ans',
  },
  {
    id: 'priorite-droite',
    cat: 'priorite',
    infraction: 'Non-respect de la priorité à droite',
    article: 'R415-5',
    amende: '135 €',
    classe: '4',
    points: -4,
    complement: 'Suspension jusqu\'à 3 ans',
  },
  {
    id: 'pieton',
    cat: 'priorite',
    infraction: 'Non-respect de la priorité à un piéton (passage protégé)',
    article: 'R415-11',
    amende: '135 €',
    classe: '4',
    points: -6,
    complement: 'Suspension jusqu\'à 3 ans',
  },
  {
    id: 'ligne-continue',
    cat: 'priorite',
    infraction: 'Franchissement ou chevauchement d\'une ligne continue',
    article: 'R412-19',
    amende: '135 €',
    classe: '4',
    points: -3,
    complement: 'Suspension jusqu\'à 3 ans',
  },
  {
    id: 'sens-interdit',
    cat: 'priorite',
    infraction: 'Circulation en sens interdit',
    article: 'R412-28',
    amende: '135 €',
    classe: '4',
    points: -4,
    complement: 'Suspension jusqu\'à 3 ans',
  },

  // === ÉQUIPEMENT MOTARD ===
  {
    id: 'casque',
    cat: 'equipement',
    infraction: 'Défaut de port du casque homologué',
    article: 'R431-1',
    amende: '135 € (minoré 90 €)',
    classe: '4',
    points: -3,
    complement: 'Immobilisation possible',
    note: 'Casque obligatoire et conforme ECE 22-05 ou 22-06, jugulaire attachée.',
  },
  {
    id: 'gants',
    cat: 'equipement',
    infraction: 'Défaut de port des gants homologués CE (conducteur et passager)',
    article: 'R431-1-2',
    amende: '68 € (minoré 45 €)',
    classe: '3',
    points: -1,
    complement: '—',
    note: 'Obligation depuis le 20 novembre 2016 (décret 2016-1232).',
  },
  {
    id: 'gilet-jaune',
    cat: 'equipement',
    infraction: 'Défaut de gilet de haute visibilité (panne / arrêt hors agglo)',
    article: 'R416-19',
    amende: '135 €',
    classe: '4',
    points: null,
    complement: '—',
  },

  // === TÉLÉPHONE & DISTRACTION ===
  {
    id: 'tel-main',
    cat: 'telephone',
    infraction: 'Téléphone tenu en main au volant / au guidon',
    article: 'R412-6-1',
    amende: '135 € (minoré 90 € · majoré 375 €)',
    classe: '4',
    points: -3,
    complement: 'Rétention immédiate du permis si infraction additionnelle',
  },
  {
    id: 'oreillettes',
    cat: 'telephone',
    infraction: 'Port à l\'oreille d\'un dispositif émettant du son (oreillettes, casque audio)',
    article: 'R412-6-1',
    amende: '135 €',
    classe: '4',
    points: -3,
    complement: '—',
    note: 'Bluetooth intégré au casque moto autorisé. Oreillettes filaires ou intra-auriculaires interdites.',
  },

  // === DOCUMENTS & ADMINISTRATIF ===
  {
    id: 'defaut-assurance',
    cat: 'documents',
    infraction: 'Défaut d\'assurance (RC obligatoire)',
    article: 'L324-2 (Code de la route) · L211-1 (Code des assurances)',
    amende: '500 € forfait (jusqu\'à 3 750 € en récidive ou audience)',
    classe: 'Délit',
    points: null,
    complement: 'Immobilisation, confiscation possible',
    note: 'L\'assurance RC est obligatoire pour tout véhicule terrestre à moteur, y compris EDPM.',
  },
  {
    id: 'defaut-permis',
    cat: 'documents',
    infraction: 'Conduite sans permis valide',
    article: 'L221-2',
    amende: '15 000 € + 1 an de prison',
    classe: 'Délit',
    points: null,
    complement: 'Confiscation du véhicule, immobilisation',
  },
  {
    id: 'defaut-carte-grise',
    cat: 'documents',
    infraction: 'Défaut de présentation de la carte grise (certificat d\'immatriculation)',
    article: 'R322-4',
    amende: '11 € (forfait à présenter sous 5 j ; 135 € sinon)',
    classe: '1',
    points: null,
    complement: '—',
  },
  {
    id: 'changement-adresse',
    cat: 'documents',
    infraction: 'Changement d\'adresse non déclaré (> 1 mois)',
    article: 'R322-7',
    amende: '135 €',
    classe: '4',
    points: null,
    complement: '—',
  },

  // === VÉHICULE & TECHNIQUE ===
  {
    id: 'pot-non-homologue',
    cat: 'vehicule',
    infraction: 'Pot d\'échappement non homologué (silencieux modifié)',
    article: 'R318-3',
    amende: '135 €',
    classe: '4',
    points: null,
    complement: 'Immobilisation, mise en conformité obligatoire',
    note: 'Arrêté du 4 mai 1995 modifié — niveau sonore et marquage CEE.',
  },
  {
    id: 'eclairage-defectueux',
    cat: 'vehicule',
    infraction: 'Éclairage / signalisation défectueux',
    article: 'R313-1 et suivants',
    amende: '68 € (3ᵉ classe) à 135 € (4ᵉ classe selon dispositif)',
    classe: '3 / 4',
    points: null,
    complement: 'Immobilisation possible',
  },
  {
    id: 'pneu-uses',
    cat: 'vehicule',
    infraction: 'Pneus usés au-delà des limites légales (TWI atteint)',
    article: 'R314-1',
    amende: '135 €',
    classe: '4',
    points: null,
    complement: 'Immobilisation',
    note: 'Profondeur minimale 1 mm sur toute la bande de roulement (ETRTO).',
  },

  // === DÉLITS ROUTIERS ===
  {
    id: 'delit-fuite',
    cat: 'delits',
    infraction: 'Délit de fuite après accident',
    article: 'L231-1',
    amende: '75 000 € + 3 ans de prison',
    classe: 'Délit',
    points: -6,
    complement: 'Suspension jusqu\'à 5 ans, annulation possible',
  },
  {
    id: 'mise-en-danger',
    cat: 'delits',
    infraction: 'Mise en danger délibérée d\'autrui',
    article: 'L223-1 (Code pénal art. 223-1)',
    amende: '15 000 € + 1 an de prison',
    classe: 'Délit',
    points: -6,
    complement: 'Suspension, annulation possible',
  },
  {
    id: 'homicide-involontaire',
    cat: 'delits',
    infraction: 'Homicide involontaire avec circonstances aggravantes (alcool, stups, vitesse)',
    article: 'L232-1 (Code pénal art. 221-6-1)',
    amende: '150 000 € + jusqu\'à 10 ans de prison',
    classe: 'Délit',
    points: -6,
    complement: 'Annulation du permis, interdiction de repasser',
  },
  {
    id: 'recidive-vitesse',
    cat: 'delits',
    infraction: 'Récidive d\'excès de vitesse ≥ 50 km/h dans les 3 ans',
    article: 'L413-1',
    amende: '3 750 € + 3 mois de prison',
    classe: 'Délit',
    points: -6,
    complement: 'Confiscation du véhicule, suspension, annulation',
  },
];

export const NB_SANCTIONS = SANCTIONS.length;
